import {
  AllTemplatesForBuilderDocument,
  GetVersionsDocument,
  TemplateTypeEnum,
  useUpdateTemplateMutation,
} from '@/__generated__/graphql';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { useSnackBarMessage } from '../utils';
import { Delete, Edit } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { EmptyStatePlaceholder } from '../../Analysis/shared/EmptyStatePlaceholder';
import Button from '../../shared/Button';
import { querySummaries, selectedQuerySummary, selectedTemplateId } from '../state';
import { TemplateFields } from './shared';
import { Field, FieldPanel, FieldsList, FormHeader, LineBreak, tooltipStyles } from './styles';

export const QuerySummaryTemplateForm = ({
  emptyForm,
  handleEmptyForm,
  structureId,
}: {
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
  structureId: string;
}) => {
  const templateId = useAtomValue(selectedTemplateId);
  const [allQuerySummaries, setAllQuerySummaries] = useAtom(querySummaries);
  const [selectedQuerySummaryItem, setSelectedSummaryQueryItem] = useAtom(selectedQuerySummary);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('Edit');

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const handleRemoveItem = () => {
    if (selectedQuerySummaryItem) {
      setAllQuerySummaries((previous) => {
        if (!previous) return undefined;
        return previous.filter((item) => item.id !== selectedQuerySummaryItem.id);
      });
      setSelectedSummaryQueryItem(allQuerySummaries?.at(0));
    }
  };

  const [updateTemplateField] = useUpdateTemplateMutation({
    refetchQueries: [AllTemplatesForBuilderDocument],
    onCompleted: (response) => {
      if (!response?.updateTemplate) {
        showSnackBar('An error occurred while updating template', 'error');
        return;
      }
      showSnackBar('Template saved successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const handleSaveTemplate = useCallback(() => {
    if (selectedQuerySummaryItem) {
      const querySummary =
        allQuerySummaries?.map((summaryItem) => {
          const item = { ...summaryItem };
          delete item.__typename;
          if (item.displayConfig) {
            const displayConfig = item.displayConfig.map((displayItem) => {
              const config = { ...displayItem };
              delete config.__typename;
              return config;
            });
            item.displayConfig = displayConfig;
          }
          return item;
        }) ?? [];
      updateTemplateField({
        variables: {
          input: {
            type: TemplateTypeEnum.Assembly,
            structureId,
            templateId: templateId ?? '',
            querySummary,
            lastModifiedFor: 'Remove Query Summary',
          },
        },
        refetchQueries: [GetVersionsDocument],
      });
    }
  }, [allQuerySummaries, selectedQuerySummaryItem, structureId, templateId, updateTemplateField]);

  if (!selectedQuerySummaryItem)
    return (
      <Box margin="6.4rem 0">
        <EmptyStatePlaceholder type="template" />
      </Box>
    );

  return (
    <Stack>
      <FormHeader>
        {emptyForm ? (
          <TemplateFields
            value={''}
            placeholder="Query Summary Title"
            mode={emptyForm ? 'Edit' : mode}
            onChange={(value) => {
              setSelectedSummaryQueryItem({ ...selectedQuerySummaryItem, name: value });
            }}
          />
        ) : (
          <Typography fontSize="2.2rem" fontWeight={500}>
            {selectedQuerySummaryItem.name}
          </Typography>
        )}

        {mode === 'Edit' || emptyForm ? (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={() => {
                if (emptyForm) {
                  handleEmptyForm(false);
                } else {
                  handleSaveTemplate();
                }
              }}
            >
              Save
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
            <Tooltip
              title="Edit Template"
              placement="top"
              arrow
              slotProps={{
                popper: {
                  sx: { tooltipStyles },
                },
              }}
            >
              <IconButton size="medium" onClick={() => setMode('Edit')}>
                <Edit
                  fontSize="inherit"
                  sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </FormHeader>

      <LineBreak marginBottom={2} />
      <Stack direction="row" width="100%" gap={2}>
        <FieldsList>
          <Stack gap={1}>
            {allQuerySummaries?.map((summaryItem) => (
              <Field
                selected={selectedQuerySummaryItem?.id === summaryItem.id}
                key={summaryItem.id}
                onClick={() => {
                  setSelectedSummaryQueryItem(summaryItem);
                }}
              >
                {summaryItem.name}
              </Field>
            ))}
          </Stack>
        </FieldsList>
        {selectedQuerySummaryItem && (
          <FieldPanel gap={3} direction="row">
            <Stack width={'50%'} gap={1}>
              {(mode === 'Edit' || emptyForm) && (
                <Button
                  sx={{ width: '50%', height: '40px' }}
                  startIcon={<Delete />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    handleRemoveItem();
                  }}
                >
                  Remove Summary Item
                </Button>
              )}
            </Stack>
          </FieldPanel>
        )}
      </Stack>
    </Stack>
  );
};
